import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  approveProperty,
  rejectProperty,
  requestRevision,
  approveDocumentation,
  rejectDocumentation,
} from '../../Redux/Actions/actions';
import style from './ApprovalActions.module.css';

const normalizeUserType = (value) => {
  const v = String(value || '').trim().toUpperCase();
  if (v === 'MARTILLER') return 'MARTILLER';
  if (v === 'SELLER' || v === 'VENDEDOR') return 'SELLER';
  return 'SELLER';
};

// Gating por email (mismo patrón de listas que SideBar).
// Documentación: martillero. Alta en el sistema: administrativos + martillero.
const allowedDocumentation = ['jgarcia@byraices.com'];
const allowedAlta = [
  'arosa@byraices.com',
  'dsanesteban@byraices.com',
  'tsanesteban@byraices.com',
  'jgarcia@byraices.com',
];

const ApprovalActions = ({ property, propertyId: propertyIdProp, onActionComplete, onRefresh }) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.userId);
  const user = useSelector((state) => state.user);
  const userType = useSelector((state) => state.userType);
  const versioningLoading = useSelector((state) => state.versioningLoading);
  const [activeModal, setActiveModal] = useState(null); // 'approve' | 'reject' | 'revision' | 'approveDoc' | 'rejectDoc'
  const [mensaje, setMensaje] = useState('');

  const propertyId = property?.id ?? propertyIdProp;
  const documentationApproved = property?.documentationApproved ?? false;

  const currentUserId = user?.user?.id || userId || null;
  const currentUserType = normalizeUserType(user?.user?.type || user?.type || userType);
  const currentMail = user?.user?.mail || null;

  const canApproveDoc = !!currentMail && allowedDocumentation.includes(currentMail);
  const canAlta = !!currentMail && allowedAlta.includes(currentMail);

  const afterAction = () => {
    setActiveModal(null);
    setMensaje('');
    if (onRefresh) onRefresh();
    if (onActionComplete) onActionComplete();
  };

  const buildAuthor = () => ({
    autorTipo: currentUserType,
    autorId: currentUserId,
    ...(currentUserType === 'SELLER' ? { sellerId: currentUserId } : { martillerId: currentUserId }),
  });

  const handleApprove = async () => {
    try {
      await dispatch(approveProperty(propertyId, {
        ...buildAuthor(),
        mensaje: mensaje.trim() || 'Propiedad aprobada',
      }));
      alert('Propiedad dada de alta correctamente.');
      afterAction();
    } catch (error) {
      if (
        error?.response?.status === 409 &&
        error?.response?.data?.code === 'DOCUMENTATION_NOT_APPROVED'
      ) {
        alert('Primero el martillero tiene que aprobar la documentación.');
        return;
      }
      alert('Error al dar de alta la propiedad.');
    }
  };

  const handleReject = async () => {
    if (!mensaje.trim()) {
      alert('Debe ingresar un motivo para rechazar.');
      return;
    }
    try {
      await dispatch(rejectProperty(propertyId, {
        ...buildAuthor(),
        mensaje: mensaje.trim(),
      }));
      alert('Propiedad rechazada. Se notificará al agente.');
      afterAction();
    } catch {
      alert('Error al rechazar la propiedad.');
    }
  };

  const handleRevision = async () => {
    if (!mensaje.trim()) {
      alert('Debe ingresar instrucciones para la revisión.');
      return;
    }
    try {
      await dispatch(requestRevision(propertyId, {
        ...buildAuthor(),
        mensaje: mensaje.trim(),
      }));
      alert('Revisión solicitada al agente.');
      afterAction();
    } catch {
      alert('Error al solicitar la revisión.');
    }
  };

  const handleApproveDoc = async () => {
    try {
      await dispatch(approveDocumentation(propertyId, {
        ...buildAuthor(),
        mensaje: mensaje.trim() || 'Documentación aprobada',
      }));
      alert('Documentación aprobada. La propiedad queda pendiente de alta.');
      afterAction();
    } catch {
      alert('Error al aprobar la documentación.');
    }
  };

  const handleRejectDoc = async () => {
    if (!mensaje.trim()) {
      alert('Debe ingresar un motivo para rechazar la documentación.');
      return;
    }
    try {
      await dispatch(rejectDocumentation(propertyId, {
        ...buildAuthor(),
        mensaje: mensaje.trim(),
      }));
      alert('Documentación rechazada. La propiedad se dio de baja del sitio.');
      afterAction();
    } catch {
      alert('Error al rechazar la documentación.');
    }
  };

  const openModal = (type) => {
    setActiveModal(type);
    setMensaje('');
  };

  const closeModal = () => {
    setActiveModal(null);
    setMensaje('');
  };

  const confirmHandlers = {
    approve: handleApprove,
    reject: handleReject,
    revision: handleRevision,
    approveDoc: handleApproveDoc,
    rejectDoc: handleRejectDoc,
  };

  const modalTitles = {
    approve: 'Dar de alta la propiedad',
    reject: 'Rechazar ficha',
    revision: 'Solicitar revisión',
    approveDoc: 'Aprobar documentación',
    rejectDoc: 'Rechazar documentación',
  };

  const modalPlaceholders = {
    approve: 'Mensaje de alta (opcional)',
    reject: 'Motivo del rechazo (obligatorio)',
    revision: 'Instrucciones de revisión (obligatorio)',
    approveDoc: 'Mensaje de aprobación (opcional)',
    rejectDoc: 'Motivo del rechazo (obligatorio)',
  };

  if (!canApproveDoc && !canAlta) return null;

  return (
    <div className={style.container}>
      <div className={style.buttonRow}>
        {canApproveDoc && (
          <>
            <button
              className={style.docApproveBtn}
              onClick={() => openModal('approveDoc')}
              disabled={versioningLoading || documentationApproved}
              title={documentationApproved ? 'La documentación ya fue aprobada' : ''}
            >
              📄 Aprobar documentación
            </button>
            <button
              className={style.docRejectBtn}
              onClick={() => openModal('rejectDoc')}
              disabled={versioningLoading}
            >
              📄 Rechazar documentación
            </button>
          </>
        )}
        {canAlta && (
          <>
            <button
              className={style.approveBtn}
              onClick={() => openModal('approve')}
              disabled={versioningLoading || !documentationApproved}
              title={!documentationApproved ? 'Falta aprobar la documentación' : ''}
            >
              ✓ Dar de alta
            </button>
            <button
              className={style.rejectBtn}
              onClick={() => openModal('reject')}
              disabled={versioningLoading}
            >
              ✕ Rechazar ficha
            </button>
            <button
              className={style.revisionBtn}
              onClick={() => openModal('revision')}
              disabled={versioningLoading}
            >
              ↻ Solicitar Revisión
            </button>
          </>
        )}
      </div>

      {activeModal && (
        <div className={style.modal}>
          <div className={style.modalContent}>
            <h4 className={style.modalTitle}>{modalTitles[activeModal]}</h4>
            {activeModal === 'rejectDoc' && (
              <p className={style.modalWarning}>
                ⚠ Si la propiedad estaba publicada, se dará de baja del sitio.
              </p>
            )}
            <textarea
              value={mensaje}
              onChange={(e) => setMensaje(e.target.value)}
              placeholder={modalPlaceholders[activeModal]}
              className={style.modalTextarea}
              rows={3}
              required={activeModal === 'reject' || activeModal === 'revision' || activeModal === 'rejectDoc'}
            />
            <div className={style.modalActions}>
              <button className={style.cancelBtn} onClick={closeModal}>
                Cancelar
              </button>
              <button
                className={
                  activeModal === 'approve' || activeModal === 'approveDoc' ? style.confirmApprove :
                  activeModal === 'revision' ? style.confirmRevision :
                  style.confirmReject
                }
                onClick={confirmHandlers[activeModal]}
                disabled={versioningLoading}
              >
                {versioningLoading ? 'Procesando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalActions;
